import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, FeatureCard } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const MissionPage: React.FC = () => {
  const theme = useTheme();

  const goals = [
    {
      icon: "🎯",
      title: "Accessible Healthcare",
      description: "Make quality healthcare accessible to everyone, regardless of location, income, or circumstances.",
    },
    {
      icon: "🤝",
      title: "Trusted Connections",
      description: "Build a platform where patients can trust they're receiving care from qualified, verified professionals.",
    },
    {
      icon: "💡",
      title: "Innovation in Care",
      description: "Leverage technology to improve healthcare delivery and patient outcomes.",
    },
    {
      icon: "🌍",
      title: "Community Impact",
      description: "Create positive change in communities by improving healthcare access and quality.",
    },
  ];

  return (
    <PageLayout
      title="Our Mission & Vision"
      subtitle="Learn about our commitment to revolutionizing healthcare delivery and improving patient outcomes."
      breadcrumbs={[
        { label: "Company", href: "/company" },
        { label: "Our Mission" },
      ]}
    >
      {/* Mission Statement */}
      <ContentSection
        title="Our Mission"
        subtitle="To revolutionize healthcare delivery by creating a trusted platform that connects patients with qualified caregivers, making quality care accessible, convenient, and affordable for everyone."
        backgroundColor={theme.palette.grey[50]}
      >
        <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
            }}
          >
            We believe that everyone deserves access to quality healthcare, delivered by qualified professionals, 
            in a way that's convenient and affordable. Our platform makes this vision a reality by connecting 
            patients with verified caregivers and providing the tools and support needed for successful care delivery.
          </Typography>
        </Box>
      </ContentSection>

      {/* Goals */}
      <ContentSection
        title="Our Goals"
        subtitle="These goals drive our work and shape our vision for the future of healthcare."
      >
        <Grid container spacing={4}>
          {goals.map((goal, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    fontSize: '3rem',
                    mb: 2,
                  }}
                >
                  {goal.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {goal.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {goal.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Join Our Mission"
        subtitle="Help us revolutionize healthcare delivery and make quality care accessible to everyone."
        primaryButton={{
          text: "Get Started",
          href: "/auth",
        }}
        secondaryButton={{
          text: "Learn More",
          href: "/about",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
